import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Settings, Share, Pencil, Sparkles, LayoutGrid, ArrowUp, ChevronLeft, X, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useCircle } from '../hooks/useCircle';
import { useArtChat } from '../hooks/useArtChat';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { subscribeToCircleDoc, setActiveArtwork } from '../services/artService';
import { db } from '../services/firebase';
import MessageBubble from '../components/MessageBubble';
import ThinkingDots from '../components/ThinkingDots';
import SettingsDrawer from '../components/SettingsDrawer';
import InviteShareModal from '../components/InviteShareModal';
import DrawingCanvas from '../components/DrawingCanvas';
import GalleryView from '../components/GalleryView';
import UpgradeModal from '../components/UpgradeModal';
import MyGalleryModal from '../components/MyGalleryModal';
import AccountUpgradeModal from '../components/AccountUpgradeModal';
import GameModal from '../components/GameModal';
import GamePickerModal from '../components/GamePickerModal';
import PassTurnModal from '../components/PassTurnModal';
import { FAMILY_GAMES } from '../data/familyGames';
import { startGame as startGameService, endGame as endGameService } from '../services/circleService';
import { generatePrompt } from '../services/gamePromptService';
import { Gamepad2 } from 'lucide-react';

export default function ArtChatPage() {
  const navigate = useNavigate();
  const { currentUser, linkAccountToGoogle } = useAuth();
  const { currentCircle, activeCircleId, hasCircles, circlesLoading } = useCircle();
  const [circleDoc, setCircleDoc] = useState(null);
  const { messages, isLoading, sendMessage, uploadAndPostDrawing, styleTransfer, saveToGallery, limitReached, dismissLimitReached, showUpgradePrompt, setShowUpgradePrompt } = useArtChat(activeCircleId, circleDoc?.activeGame, currentCircle);
  const { generationsUsed, generationsLimit, remaining, isLifetimeLimit, isPaid, planId } = useSubscription();
  const [input, setInput] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showDrawing, setShowDrawing] = useState(false);
  const [drawingImageUrl, setDrawingImageUrl] = useState(null);
  const [viewMode, setViewMode] = useState('chat');
  const [galleryArtworks, setGalleryArtworks] = useState([]);
  const [editTargetImageUrl, setEditTargetImageUrl] = useState(null);
  const [showMyGallery, setShowMyGallery] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [showPassTurn, setShowPassTurn] = useState(false);

  const activeGame = currentCircle?.activeGame || null;
  // #region agent log
  fetch('http://127.0.0.1:7902/ingest/4a95ddbd-91e2-4624-b2fe-a19b8fa8dc8d',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'3a6109'},body:JSON.stringify({sessionId:'3a6109',location:'ArtChatPage.jsx:activeGame',message:'activeGame state',data:{hasActiveGame:!!activeGame,gameId:activeGame?.gameId,currentRound:activeGame?.currentRound,currentDrawer:activeGame?.currentDrawer,hasCurrentCircle:!!currentCircle,currentCircleKeys:currentCircle?Object.keys(currentCircle):null},timestamp:Date.now(),hypothesisId:'H3'})}).catch(()=>{});
  // #endregion

  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const inputRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const hasScrolledOnMount = useRef(false);

  const ADMIN_UID = 'o2VVqpqGLvcPeXiNYe8FdGhXnBJ3';
  const isAdmin = currentUser?.uid === ADMIN_UID;
  const isAdminCircle = currentCircle?.createdBy === ADMIN_UID;
  const isUnlimited = isAdmin || isAdminCircle || planId === 'family_pro';

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const el = messageAreaRef.current;
    if (!el) return;
    const threshold = 80;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setIsAtBottom(atBottom);
  }, []);

  useEffect(() => {
    if (messages.length > 0 && !hasScrolledOnMount.current) {
      hasScrolledOnMount.current = true;
      requestAnimationFrame(() => scrollToBottom('instant'));
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom('smooth');
    }
  }, [messages, isLoading, isAtBottom, scrollToBottom]);

  useEffect(() => {
    if (!activeCircleId) return;
    return subscribeToCircleDoc(activeCircleId, setCircleDoc);
  }, [activeCircleId]);

  useEffect(() => {
    if (!circleDoc?.activeArtworkStartedAt || !activeCircleId) return;
    const startedAt = circleDoc.activeArtworkStartedAt;
    const startedMs = startedAt.seconds ? startedAt.seconds * 1000 : startedAt.toMillis?.() ?? 0;
    const ageMs = Date.now() - startedMs;
    const ONE_HOUR = 60 * 60 * 1000;
    if (ageMs > ONE_HOUR) {
      setActiveArtwork(activeCircleId, false).catch(console.error);
    }
  }, [circleDoc?.activeArtworkStartedAt, activeCircleId]);

  useEffect(() => {
    if (!activeCircleId) return;
    const q = query(
      collection(db, 'galleryArtworks'),
      where('circleId', '==', activeCircleId),
      orderBy('savedAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      setGalleryArtworks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('Gallery subscription error:', error);
    });
  }, [activeCircleId]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    const editUrl = editTargetImageUrl;
    setInput('');
    setEditTargetImageUrl(null);
    if (editUrl) {
      sendMessage(text, { imageUrl: editUrl, intent: 'edit' });
    } else {
      sendMessage(text);
    }
  };

  const handleCreateWithAI = () => {
    setInput('@Ross ');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEditWithRoss = (imageUrl) => {
    setInput('');
    sendMessage('@Ross Edit this image', { imageUrl, intent: 'edit' });
  };

  const handleEditWithRossPrefill = (imageUrl) => {
    setEditTargetImageUrl(imageUrl || null);
    setInput('@Ross ');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSaveToGallery = (message) => {
    saveToGallery(message);
  };

  const handleOpenDrawOnImage = (imageUrl) => {
    setDrawingImageUrl(imageUrl);
    setShowDrawing(true);
  };

  const handleOpenBlankCanvas = () => {
    setDrawingImageUrl(null);
    setShowDrawing(true);
  };

  const handleDrawingSave = async (blob) => {
    setShowDrawing(false);
    setDrawingImageUrl(null);
    await uploadAndPostDrawing(blob);
  };

  const handleDrawingClose = () => {
    setShowDrawing(false);
    setDrawingImageUrl(null);
  };

  const handleStyleTransfer = (imageUrl, style) => {
    setViewMode('chat');
    styleTransfer(imageUrl, style);
  };

  const handleSelectFromGallery = (imageUrl) => {
    setEditTargetImageUrl(imageUrl);
    setInput('@Ross ');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    if (!circlesLoading && !hasCircles) navigate('/circles', { replace: true });
  }, [circlesLoading, hasCircles, navigate]);

  const handleAccountUpgrade = async () => {
    try {
      await linkAccountToGoogle();
      setShowUpgradePrompt(false);
    } catch (err) {
      console.error('Failed to upgrade account:', err);
      
      // If credential already in use, suggest signing in with existing account
      if (err.code === 'credential-already-in-use') {
        alert('This Google account is already in use. Please sign out and sign in with your existing account to keep all your data.');
      } else {
        alert('Failed to link account. Please try again.');
      }
    }
  };

  const handleStartGame = async (config) => {
    setSelectedGame(null);

    let prompt = config.prompt || null;
    if (config.gameId === 'sketch-guess' && !prompt) {
      prompt = await generatePrompt('sketch-guess');
    }

    await startGameService(activeCircleId, {
      ...config,
      prompt,
      currentDrawer: currentUser.uid,
    });

    if (config.gameId === 'sketch-guess') {
      sendMessage(`🎮 Let's play Sketch & Guess! I got my secret prompt. Time to draw! (${config.rounds} round${config.rounds > 1 ? 's' : ''})`);
    } else if (config.gameId === 'art-relay') {
      sendMessage(`🏓 Starting Art Relay! ${config.rounds} round${config.rounds > 1 ? 's' : ''} around the circle. First person, start drawing!`);
    } else if (config.gameId === 'scene-builder') {
      sendMessage(`🏗️ Scene Builder: "${config.scenePrompt}" — First person, draw the setting! Then everyone adds something.`);
    } else if (config.gameId === 'dream-vacation') {
      sendMessage(`✈️ Dream Vacation time! Everyone draw your dream family trip — the place, an activity, a food, and a surprise.`);
    } else if (config.gameId === 'monster-mash') {
      sendMessage(`👾 Monster Mash! Round 1: Draw the HEAD. Round 2: Body. Round 3: Legs/arms. Round 4: Accessories & color. Let's go!`);
    } else if (config.gameId === 'emoji-challenge') {
      sendMessage(`😎 Emoji Art Challenge! Your emojis: ${config.emojis.join(' ')} — Draw a scene that includes ALL of them!`);
    } else if (config.gameId === 'portrait-swap') {
      sendMessage(`🖼️ Portrait Swap! Pick someone in the circle and draw their portrait — any style goes! Silly, superhero, cartoon animal... make them smile.`);
    }
  };

  if (circlesLoading || !hasCircles) return null;

  return (
    <Container>
      <Header>
        {viewMode === 'gallery' ? (
          <>
            <BackButton onClick={() => setViewMode('chat')}>
              <ChevronLeft size={22} strokeWidth={2.5} />
              <BackLabel>Chat</BackLabel>
            </BackButton>
            <HeaderCenter>
              <CircleName>Gallery</CircleName>
            </HeaderCenter>
            <HeaderActions>
              <IconButton onClick={() => setShowInvite(true)} title="Share invite">
                <Share size={20} />
              </IconButton>
            </HeaderActions>
          </>
        ) : (
          <>
            <IconButton onClick={() => setShowSettings(true)} title="Settings">
              <Settings size={20} />
            </IconButton>
            <HeaderInfo>
              <CircleName>{currentCircle?.name || 'Loading...'}</CircleName>
              <CircleMeta>
                {currentCircle?.memberIds?.length || 0} members
                {' · '}
                {messages.filter((m) => m.isNewArtwork).length} artworks
                {isUnlimited ? null : (
                  <>
                    {' · '}
                    <GenCounter $warning={remaining <= 3 && remaining > 0} $empty={remaining === 0}>
                      {remaining === 0 ? '0' : remaining} gen{remaining !== 1 ? 's' : ''} left
                    </GenCounter>
                  </>
                )}
              </CircleMeta>
            </HeaderInfo>
            <HeaderActions>
              <IconButton onClick={() => setViewMode('gallery')} title="Gallery">
                <LayoutGrid size={20} />
              </IconButton>
              <IconButton onClick={() => setShowInvite(true)} title="Share invite">
                <Share size={20} />
              </IconButton>
            </HeaderActions>
          </>
        )}
      </Header>

      {viewMode === 'gallery' ? (
        <GalleryView
          messages={messages}
          galleryArtworks={galleryArtworks}
          circleId={activeCircleId}
          onDrawOnThis={handleOpenDrawOnImage}
          onEditWithRoss={handleEditWithRoss}
          onStyleTransfer={handleStyleTransfer}
        />
      ) : (
        <>
          <MessageArea ref={messageAreaRef} onScroll={handleScroll}>
            {messages.length === 0 && (
              <WelcomeSection>
                <WelcomeLogo src="/ross.png" alt="Ross" />
                <WelcomeTitle>Hi! I'm Ross, your art teacher.</WelcomeTitle>
                <WelcomeText>
                  Pick a game to play with your family, or just start creating!
                  I can help you draw anything — just describe it and I'll make it happen.
                </WelcomeText>

                <GameSection>
                  <GameSectionLabel>
                    <Gamepad2 size={14} />
                    Family Games
                  </GameSectionLabel>
                  <GameChips>
                    {FAMILY_GAMES.map((game) => (
                      <GameChip key={game.id} onClick={() => setSelectedGame(game)}>
                        <GameChipEmoji>{game.emoji}</GameChipEmoji>
                        <GameChipText>
                          <GameChipName>{game.name}</GameChipName>
                          <GameChipDesc>{game.shortDesc}</GameChipDesc>
                        </GameChipText>
                      </GameChip>
                    ))}
                  </GameChips>
                </GameSection>

                <WelcomeDivider>or just start creating</WelcomeDivider>

                <FreeplayButtons>
                  <WelcomeDrawButton onClick={handleOpenBlankCanvas}>
                    <Pencil size={16} />
                    <span>Draw by Hand</span>
                  </WelcomeDrawButton>
                  <WelcomeDrawButton onClick={handleCreateWithAI}>
                    <Sparkles size={16} />
                    <span>Create with AI</span>
                  </WelcomeDrawButton>
                </FreeplayButtons>
              </WelcomeSection>
            )}

            {(() => {
              const latestArtworkIdx = messages.reduce((acc, m, i) => m.isNewArtwork ? i : acc, -1);
              return messages.map((msg, idx) => (
                <MessageBubble
                  key={msg.id || msg.timestamp}
                  message={msg}
                  currentUserId={currentUser?.uid}
                  memberInfo={currentCircle?.memberInfo}
                  onDrawOnThis={handleOpenDrawOnImage}
                  onEditWithRoss={handleEditWithRoss}
                  onEditWithRossPrefill={handleEditWithRossPrefill}
                  onSaveToGallery={() => handleSaveToGallery(msg)}
                  isLatestArtwork={idx === latestArtworkIdx}
                  activeGame={circleDoc?.activeGame}
                  currentCircle={currentCircle}
                />
              ));
            })()}

            {isLoading && (
              <LoadingRow>
                <ThinkingDots />
              </LoadingRow>
            )}

            <div ref={messagesEndRef} />
          </MessageArea>

          {!isAtBottom && messages.length > 0 && (
            <ScrollToBottomButton onClick={() => scrollToBottom('smooth')}>
              <ChevronDown size={20} strokeWidth={2.5} />
            </ScrollToBottomButton>
          )}

          <PickerBar>
            <ModeButton onClick={handleCreateWithAI} disabled={isLoading}>
              <Sparkles size={18} />
              <span>Create with AI</span>
            </ModeButton>
            <ModeButton onClick={handleOpenBlankCanvas} disabled={isLoading}>
              <Pencil size={18} />
              <span>Draw by Hand</span>
            </ModeButton>
            <ModeButton onClick={() => setShowMyGallery(true)} disabled={isLoading}>
              <ImageIcon size={18} />
              <span>My Gallery</span>
            </ModeButton>
            <ModeButton onClick={() => setShowGamePicker(true)} disabled={isLoading}>
              <Gamepad2 size={18} />
              <span>Games</span>
            </ModeButton>
          </PickerBar>

          {activeGame && (() => {
            const gameMeta = FAMILY_GAMES.find(g => g.id === activeGame.gameId);
            const isDrawer = activeGame.currentDrawer === currentUser?.uid;
            const drawerName = currentCircle?.memberInfo?.[activeGame.currentDrawer]?.displayName || 'Someone';
            const roundLabel = activeGame.totalRounds > 1 ? `Round ${activeGame.currentRound} of ${activeGame.totalRounds}` : null;

            return (
              <ActiveGameBar>
                <ActiveGameInfo>
                  <span>{gameMeta?.emoji}</span>
                  <ActiveGameName>
                    {gameMeta?.name}
                    {roundLabel && <ActiveGameRound>{roundLabel}</ActiveGameRound>}
                    {isDrawer && activeGame.prompt && (
                      <ActiveGamePrompt>Your prompt: {activeGame.prompt}</ActiveGamePrompt>
                    )}
                    {isDrawer && activeGame.emojis && (
                      <ActiveGamePrompt>Your emojis: {activeGame.emojis.join(' ')}</ActiveGamePrompt>
                    )}
                    {activeGame.scenePrompt && (
                      <ActiveGamePrompt>Scene: {activeGame.scenePrompt}</ActiveGamePrompt>
                    )}
                    {!isDrawer && (
                      <ActiveGamePrompt>{drawerName} is drawing...</ActiveGamePrompt>
                    )}
                  </ActiveGameName>
                </ActiveGameInfo>
                {isDrawer && activeGame.totalRounds > 1 && (
                  <PassTurnButton onClick={() => setShowPassTurn(true)}>
                    Skip Turn
                  </PassTurnButton>
                )}
                <ActiveGameDismiss onClick={() => endGameService(activeCircleId)}>
                  <X size={14} />
                </ActiveGameDismiss>
              </ActiveGameBar>
            );
          })()}

          {editTargetImageUrl && (
            <EditTargetBar>
              <EditTargetThumb src={editTargetImageUrl} alt="Editing" />
              <EditTargetLabel>
                <Sparkles size={12} />
                Editing this image with AI
              </EditTargetLabel>
              <EditTargetDismiss onClick={() => setEditTargetImageUrl(null)}>
                <X size={14} />
              </EditTargetDismiss>
            </EditTargetBar>
          )}

          <InputBar>
            <InputField
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={editTargetImageUrl ? "Describe how to edit this image..." : "Message your circle... (use @Ross for AI)"}
              disabled={isLoading}
              rows={1}
            />
            <SendButton onClick={handleSend} disabled={!input.trim() || isLoading}>
              <ArrowUp size={20} strokeWidth={2.5} />
            </SendButton>
          </InputBar>
        </>
      )}

      <SettingsDrawer
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentCircle={currentCircle}
      />

      <InviteShareModal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        inviteCode={currentCircle?.inviteCode}
        circleName={currentCircle?.name}
      />

      {showDrawing && (
        <DrawingCanvas
          imageUrl={drawingImageUrl}
          onSave={handleDrawingSave}
          onClose={handleDrawingClose}
        />
      )}

      <MyGalleryModal
        isOpen={showMyGallery}
        onClose={() => setShowMyGallery(false)}
        onSelectImage={handleSelectFromGallery}
        currentUserId={currentUser?.uid}
      />

      <UpgradeModal
        isOpen={!!limitReached}
        onClose={dismissLimitReached}
        limitInfo={limitReached}
      />

      <AccountUpgradeModal
        isOpen={showUpgradePrompt}
        onUpgrade={handleAccountUpgrade}
        onDismiss={() => setShowUpgradePrompt(false)}
      />

      <GamePickerModal
        isOpen={showGamePicker}
        onClose={() => setShowGamePicker(false)}
        onSelectGame={(game) => {
          setShowGamePicker(false);
          setSelectedGame(game);
        }}
      />

      <GameModal
        game={selectedGame}
        isOpen={!!selectedGame}
        onClose={() => { setSelectedGame(null); setShowGamePicker(true); }}
        onStartGame={handleStartGame}
      />

      <PassTurnModal
        isOpen={showPassTurn}
        onClose={() => setShowPassTurn(false)}
        circleId={activeCircleId}
        currentCircle={currentCircle}
        activeGame={activeGame}
        currentUserId={currentUser?.uid}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceElevated};
  flex-shrink: 0;
  position: relative;
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
`;

const CircleName = styled.h1`
  font-size: 17px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CircleMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const GenCounter = styled.span`
  font-weight: 600;
  color: ${({ $empty, $warning, theme }) =>
    $empty ? theme.colors.error : $warning ? theme.colors.warning : 'inherit'};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 10px 12px 10px 0;
  min-height: 44px;
  color: ${({ theme }) => theme.colors.accent};
  transition: opacity 0.2s;
  z-index: 1;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }
`;

const BackLabel = styled.span`
  font-size: 17px;
  font-weight: 400;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
`;

const IconButton = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const MessageArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WelcomeSection = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const WelcomeLogo = styled.img`
  height: 80px;
  width: auto;
  display: block;
  margin: 0 auto 12px;
`;

const WelcomeTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const WelcomeText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 20px;
`;

const GameSection = styled.div`
  margin-top: 8px;
  text-align: left;
`;

const GameSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 10px;
  padding: 0 4px;
`;

const GameChips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GameChip = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: left;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surface};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GameChipEmoji = styled.span`
  font-size: 28px;
  flex-shrink: 0;
`;

const GameChipText = styled.div`
  flex: 1;
  min-width: 0;
`;

const GameChipName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const GameChipDesc = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const WelcomeDivider = styled.div`
  margin: 20px 0 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  display: flex;
  align-items: center;
  gap: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const FreeplayButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const WelcomeDrawButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ScrollToBottomButton = styled.button`
  position: absolute;
  bottom: 140px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transform: translateY(2px);
  }
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceElevated};
  flex-shrink: 0;
`;

const InputField = styled.textarea`
  flex: 1;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 15px;
  resize: none;
  outline: none;
  min-height: 40px;
  max-height: 120px;
  line-height: 1.4;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const PickerBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 8px 16px 0;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModeButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:not(:disabled):active {
    transform: scale(0.97);
  }
`;

const EditTargetBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const EditTargetThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.small};
  object-fit: cover;
`;

const EditTargetLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  flex: 1;
`;

const EditTargetDismiss = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textTertiary};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ActiveGameBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-top: 2px solid #f59e0b;
  flex-shrink: 0;
`;

const ActiveGameInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  font-size: 22px;
`;

const ActiveGameName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #92400e;
`;

const ActiveGameRound = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  margin-top: 2px;
`;

const ActiveGamePrompt = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #b45309;
  margin-top: 3px;
`;

const PassTurnButton = styled.button`
  padding: 8px 14px;
  border-radius: 20px;
  background: #f59e0b;
  color: white;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: #d97706;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ActiveGameDismiss = styled.button`
  padding: 8px;
  border-radius: 50%;
  color: #92400e;
  flex-shrink: 0;

  &:hover {
    background: rgba(245, 158, 11, 0.15);
    color: #78350f;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: scale(1.05);
  }

  &:not(:disabled):active {
    transform: scale(0.95);
  }
`;

